import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sprout, TrendingUp, ShieldCheck, Clock, Wallet, ArrowRight, Leaf } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1763396134558-0de46611ec1b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const tiers = [
  { name: "Palıd Fidanı", price: "30 ₼", income: "2 ₼ / gün", img: "https://images.unsplash.com/photo-1457530378978-8bac673b8062?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { name: "Şam Ağacı", price: "50 ₼", income: "3.5 ₼ / gün", img: "https://images.unsplash.com/photo-1625179893310-8e419f979608?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { name: "Zeytun Ağacı", price: "100 ₼", income: "8 ₼ / gün", img: "https://images.unsplash.com/photo-1676667468027-5879e97f43e4?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
];

const steps = [
  { icon: Wallet, title: "Balans artır", text: "Depozit sorğusu göndər, admin təsdiqləyəndən sonra balansın hazırdır." },
  { icon: Sprout, title: "Ağac al", text: "10, 20 və ya 30 günlük müddət seç və ağacını əldə et." },
  { icon: TrendingUp, title: "Gəlir yığ", text: "Gəlir gün ərzində toplanır, istədiyin vaxt cüzdanına yığ." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="absolute top-0 inset-x-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
              <Sprout className="w-5 h-5 text-primary-foreground" />
            </span>
            <span className="font-heading font-bold text-lg tracking-tight text-white">Yaşıl İnvest</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/10 hover:text-white rounded-full" data-testid="header-login-btn">Giriş</Button>
            </Link>
            <Link to="/register">
              <Button className="rounded-full bg-white text-primary hover:bg-white/90" data-testid="header-register-btn">Qeydiyyat</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <img src={HERO} alt="Meşə" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#050E08]/90 via-[#050E08]/70 to-[#124225]/50" />
        <div className="noise-overlay absolute inset-0 opacity-20 mix-blend-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-32 w-full">
          <div className="max-w-2xl animate-float-up">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/20 border border-primary/30 text-primary-foreground text-sm mb-6">
              <Leaf className="w-4 h-4 text-accent" /> Təbiətdən qazanc
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]">
              Ağac al, hər gün<br /><span className="text-accent">gəlir qazan.</span>
            </h1>
            <p className="mt-6 text-lg text-white/80 max-w-lg">
              Yaşıl İnvest ilə 10, 20 və ya 30 günlük ağaclara sərmayə qoy. Gəlirin gün ərzində toplanır, istədiyin vaxt cüzdanına yığ.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-8 text-base transition-transform hover:scale-[1.02]" data-testid="hero-cta-btn">
                  İndi başla <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-8 text-base bg-white/10 text-white border-white/30 hover:bg-white/20 hover:text-white" data-testid="hero-login-btn">
                  Hesabım var
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <div className="max-w-xl mb-14">
          <p className="text-accent font-semibold text-sm uppercase tracking-wide">Necə işləyir</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Üç sadə addımda gəlir</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="p-8 rounded-2xl border border-border bg-card transition-transform hover:-translate-y-1" data-testid={`step-${i}`}>
              <span className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center mb-5">
                <s.icon className="w-6 h-6 text-primary" />
              </span>
              <h3 className="font-heading text-xl font-semibold">{s.title}</h3>
              <p className="text-muted-foreground mt-2">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tiers */}
      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div className="max-w-xl">
              <p className="text-accent font-semibold text-sm uppercase tracking-wide">Ağaclar</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Populyar ağaclar</h2>
            </div>
            <Link to="/register"><Button variant="outline" className="rounded-full">Hamısına bax <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((t, i) => (
              <div key={i} className="rounded-2xl overflow-hidden border border-border bg-card group" data-testid={`tier-${i}`}>
                <div className="h-48 overflow-hidden">
                  <img src={t.img} alt={t.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold">{t.name}</h3>
                  <div className="mt-4 flex items-end justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Qiymət</p>
                      <p className="font-heading text-2xl font-bold">{t.price}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Gəlir</p>
                      <p className="font-heading text-lg font-bold text-primary">{t.income}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-muted-foreground text-sm mt-6">Daha bahalı ağaclar (300 ₼+) müddətə görə 30%–50% qazanc verir.</p>
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24 grid md:grid-cols-3 gap-6">
        {[
          { icon: Clock, t: "Çevik müddət", d: "10, 20 və ya 30 günlük planlar" },
          { icon: TrendingUp, t: "Gündəlik gəlir", d: "Gəlir hər saniyə toplanır" },
          { icon: ShieldCheck, t: "Təhlükəsiz", d: "Balans və çıxarış admin nəzarətində" },
        ].map((f, i) => (
          <div key={i} className="flex items-start gap-4">
            <span className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center shrink-0"><f.icon className="w-5 h-5 text-primary" /></span>
            <div><h3 className="font-heading font-semibold text-lg">{f.t}</h3><p className="text-muted-foreground">{f.d}</p></div>
          </div>
        ))}
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Sprout className="w-5 h-5 text-primary" />
            <span className="font-heading font-semibold">Yaşıl İnvest</span>
          </div>
          <p className="text-sm text-muted-foreground">© 2026 Yaşıl İnvest. Bütün hüquqlar qorunur.</p>
        </div>
      </footer>
    </div>
  );
}
